import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import FileUploader from "@/components/admin/FileUploader";
import { GUIDE_BASES } from "@/i18n/routeMap";
import { PUBLIC_DOMAIN } from "@/lib/seo";

const LANGS = ["en", "es", "ru"];
const VIEW_LABEL = { en: "View EN", es: "Ver ES", ru: "Открыть RU" };

const guideUrl = (g, lang) => {
  const s = (g.slug && g.slug[lang]) || (g.slug && g.slug.en);
  if (!s) return null;
  // Always open public guides on the official canonical domain — never on the
  // temporary Emergent host, even when the admin itself is served from it.
  return `${PUBLIC_DOMAIN}${GUIDE_BASES[lang]}/${s}`;
};
const blank = {
  slug: { en: "", es: "", ru: "" },
  title: { en: "", es: "", ru: "" },
  h1: { en: "", es: "", ru: "" },
  seoTitle: { en: "", es: "", ru: "" },
  metaDescription: { en: "", es: "", ru: "" },
  intro: { en: "", es: "", ru: "" },
  body: { en: "", es: "", ru: "" },
  coverImage: "",
  category: "guide",
  faqs: [],
  active: true,
  noindex: false,
  seoEnabled: true,
  order: 0,
  recommendedVehicleIds: [],
};

export default function GuidesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [activeLang, setActiveLang] = useState("en");
  const [vehicles, setVehicles] = useState([]);

  const load = async () => {
    const { data } = await api.get("/guides?includeInactive=true");
    setItems(data || []);
  };
  const loadVehicles = async () => {
    try {
      const { data } = await api.get("/vehicles");
      setVehicles(Array.isArray(data) ? data : []);
    } catch { setVehicles([]); }
  };
  useEffect(() => { load(); loadVehicles(); }, []);

  const startNew = () => { setEditing(JSON.parse(JSON.stringify(blank))); setActiveLang("en"); };
  const startEdit = (g) => {
    const merged = { ...JSON.parse(JSON.stringify(blank)) };
    ["slug","title","h1","seoTitle","metaDescription","intro","body"].forEach((k) => {
      merged[k] = { ...blank[k], ...(g[k] || {}) };
    });
    merged.faqs = (Array.isArray(g.faqs) ? g.faqs : []).map((f) => ({
      _uid: f._uid || `faq-${Math.random().toString(36).slice(2, 9)}`,
      ...f,
    }));
    merged.coverImage = g.coverImage || "";
    merged.category = g.category || "guide";
    merged.active = g.active !== false;
    merged.noindex = !!g.noindex;
    merged.seoEnabled = g.seoEnabled !== false;
    merged.order = g.order || 0;
    merged.id = g.id;
    merged.recommendedVehicleIds = Array.isArray(g.recommendedVehicleIds) ? [...g.recommendedVehicleIds] : [];
    setEditing(merged);
  };
  const save = async () => {
    const payload = { ...editing };
    if (editing.id) await api.put(`/guides/${editing.id}`, payload);
    else await api.post("/guides", payload);
    setEditing(null);
    await load();
  };
  const remove = async (id) => { if (!window.confirm("Delete this guide?")) return; await api.delete(`/guides/${id}`); await load(); };

  const addFaq = () =>
    setEditing((prev) => ({
      ...prev,
      faqs: [
        ...(prev.faqs || []),
        { _uid: `faq-${Math.random().toString(36).slice(2, 9)}`, q: { en: "", es: "", ru: "" }, a: { en: "", es: "", ru: "" } },
      ],
    }));
  const setFaq = (i, k, l, v) =>
    setEditing((prev) => ({
      ...prev,
      faqs: (prev.faqs || []).map((f, idx) => (idx === i ? { ...f, [k]: { ...(f[k] || {}), [l]: v } } : f)),
    }));
  const removeFaq = (i) =>
    setEditing((prev) => ({ ...prev, faqs: (prev.faqs || []).filter((_, idx) => idx !== i) }));

  return (
    <div data-testid="admin-guides-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-[#F5F1EB] text-3xl">Guides / SEO Content</h1>
        <button onClick={startNew} className="btn-gold" data-testid="admin-guide-new">+ New guide</button>
      </div>

      {!editing && (
        <div className="border border-[#2A2A2A]">
          {items.length === 0 && <div className="p-6 text-[#8A8A8A] text-sm">No guides yet.</div>}
          {items.map((g) => (
            <div key={g.id} className="grid grid-cols-12 px-4 py-3 border-b border-[#2A2A2A] last:border-b-0 text-sm items-center" data-testid={`admin-guide-row-${g.slug?.en}`}>
              <div className="col-span-5 text-[#F5F1EB] truncate">{g.title?.en || "(untitled)"}</div>
              <div className="col-span-3 text-[#8A8A8A] truncate">{g.slug?.en}</div>
              <div className="col-span-2">
                <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 border ${g.active ? "border-[#C9A55A]/40 text-[#C9A55A]" : "border-[#8A8A8A]/40 text-[#8A8A8A]"}`}>
                  {g.active ? "active" : "draft"}{g.noindex ? " · noindex" : ""}
                </span>
              </div>
              <div className="col-span-2 text-right flex justify-end gap-2 flex-wrap">
                {LANGS.map((lg) => {
                  const url = guideUrl(g, lg);
                  if (!url) return null;
                  const disabled = !g.active || g.noindex;
                  const title = disabled
                    ? "This guide is not public yet"
                    : `Open ${lg.toUpperCase()} page in a new tab`;
                  return (
                    <a
                      key={lg}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={title}
                      className={`text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 border ${disabled ? "border-[#8A8A8A]/30 text-[#8A8A8A]" : "border-[#C9A55A]/40 text-[#C9A55A] hover:bg-[#C9A55A] hover:text-[#0C0C0C]"}`}
                      data-testid={`admin-guide-view-${g.slug?.en}-${lg}`}
                    >
                      {VIEW_LABEL[lg]}
                    </a>
                  );
                })}
                <button onClick={() => startEdit(g)} className="text-[11px] uppercase tracking-[0.18em] text-[#C9A55A] hover:text-[#F5F1EB]" data-testid={`admin-guide-edit-${g.slug?.en}`}>Edit</button>
                <button onClick={() => remove(g.id)} className="text-[11px] uppercase tracking-[0.18em] text-[#B8342A] hover:opacity-80">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-[#F5F1EB] text-xl">{editing.id ? "Edit guide" : "New guide"}</h2>
            <div className="flex gap-2">
              <button onClick={() => setEditing(null)} className="px-4 h-10 border border-[#2A2A2A] text-[#F5F1EB] text-xs uppercase tracking-[0.18em]">Cancel</button>
              <button onClick={save} className="btn-gold" data-testid="admin-guide-save">Save</button>
            </div>
          </div>

          <div className="border border-[#2A2A2A] p-4">
            <div className="flex gap-2 mb-3">
              {LANGS.map((l) => (
                <button key={l} onClick={() => setActiveLang(l)} className={`px-3 h-8 text-xs uppercase tracking-[0.18em] border ${activeLang === l ? "border-[#C9A55A] text-[#C9A55A]" : "border-[#2A2A2A] text-[#F5F1EB]"}`}>{l}</button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <I label={`Slug (${activeLang})`} value={editing.slug[activeLang]} on={(v) => setEditing({ ...editing, slug: { ...editing.slug, [activeLang]: v.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-") } })} testid={`ga-slug-${activeLang}`} />
              <I label={`Title (${activeLang})`} value={editing.title[activeLang]} on={(v) => setEditing({ ...editing, title: { ...editing.title, [activeLang]: v } })} testid={`ga-title-${activeLang}`} />
              <I label={`H1 (${activeLang})`} value={editing.h1[activeLang]} on={(v) => setEditing({ ...editing, h1: { ...editing.h1, [activeLang]: v } })} />
              <I label={`SEO title (${activeLang})`} value={editing.seoTitle[activeLang]} on={(v) => setEditing({ ...editing, seoTitle: { ...editing.seoTitle, [activeLang]: v } })} />
            </div>
            <T label={`Meta description (${activeLang})`} value={editing.metaDescription[activeLang]} on={(v) => setEditing({ ...editing, metaDescription: { ...editing.metaDescription, [activeLang]: v } })} />
            <T label={`Intro (${activeLang})`} value={editing.intro[activeLang]} on={(v) => setEditing({ ...editing, intro: { ...editing.intro, [activeLang]: v } })} />
            <T label={`Body (HTML — use <h2>, <p>, <ul>, <a>) (${activeLang})`} value={editing.body[activeLang]} on={(v) => setEditing({ ...editing, body: { ...editing.body, [activeLang]: v } })} rows={10} testid={`ga-body-${activeLang}`} />
          </div>

          <div className="border border-[#2A2A2A] p-4">
            <div className="text-overline text-[#C9A55A] mb-3">Flags & cover</div>
            <I label="Cover image URL (or upload below)" value={editing.coverImage || ""} on={(v) => setEditing({ ...editing, coverImage: v })} />
            <FileUploader accept="image/*" onUploaded={(r) => setEditing({ ...editing, coverImage: r.url })} label="Upload cover image" />
            <div className="grid grid-cols-3 gap-3 mt-3 text-sm">
              <label className="flex items-center gap-2 text-[#F5F1EB]"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="ga-active" /> Active</label>
              <label className="flex items-center gap-2 text-[#F5F1EB]"><input type="checkbox" checked={editing.seoEnabled !== false} onChange={(e) => setEditing({ ...editing, seoEnabled: e.target.checked })} /> SEO enabled</label>
              <label className="flex items-center gap-2 text-[#F5F1EB]"><input type="checkbox" checked={!!editing.noindex} onChange={(e) => setEditing({ ...editing, noindex: e.target.checked })} data-testid="ga-noindex" /> noindex</label>
            </div>
          </div>

          <div className="border border-[#2A2A2A] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-overline text-[#C9A55A]">Recommended cars (2–3, public bottom of guide)</div>
              <span className="text-[10px] text-[#8A8A8A] uppercase tracking-[0.18em]">Leave empty → auto from catalog</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {vehicles.map((v) => {
                const selected = (editing.recommendedVehicleIds || []).includes(v.id);
                const main = (v.images || []).find((i) => i.isMain) || (v.images || [])[0];
                const toggle = () => {
                  setEditing((prev) => {
                    const cur = prev.recommendedVehicleIds || [];
                    if (cur.includes(v.id)) return { ...prev, recommendedVehicleIds: cur.filter((x) => x !== v.id) };
                    if (cur.length >= 3) return prev; // cap at 3
                    return { ...prev, recommendedVehicleIds: [...cur, v.id] };
                  });
                };
                return (
                  <button
                    type="button"
                    key={v.id}
                    onClick={toggle}
                    className={`relative text-left border ${selected ? "border-[#C9A55A]" : "border-[#2A2A2A]"} hover:border-[#C9A55A]/60 transition-colors`}
                    data-testid={`ga-rec-vehicle-${v.slug}`}
                  >
                    <div className="aspect-[4/3] bg-[#1A1A1A] overflow-hidden">
                      {main?.url && (
                        <img src={main.url} alt={v.name} className="w-full h-full object-cover" loading="lazy" />
                      )}
                    </div>
                    <div className="p-2">
                      <div className="text-[#F5F1EB] text-xs truncate">{v.name}</div>
                      <div className="text-[10px] text-[#8A8A8A] uppercase tracking-[0.18em]">{v.categoryKey}</div>
                    </div>
                    {selected && (
                      <span className="absolute top-1 right-1 bg-[#C9A55A] text-[#0C0C0C] text-[10px] px-1.5 py-0.5 uppercase tracking-[0.18em]">
                        #{(editing.recommendedVehicleIds || []).indexOf(v.id) + 1}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] text-[#8A8A8A] mt-3">
              Selected: {(editing.recommendedVehicleIds || []).length} / 3
            </div>
          </div>

          <div className="border border-[#2A2A2A] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-overline text-[#C9A55A]">FAQs</div>
              <button onClick={addFaq} className="text-xs uppercase tracking-[0.18em] text-[#C9A55A]" data-testid="ga-faq-add">+ Add FAQ</button>
            </div>
            {editing.faqs.map((f, i) => (
              <div key={f._uid || `faq-${i}`} className="border border-[#2A2A2A] p-3 mb-3" data-testid={`ga-faq-item-${i}`}>
                <div className="flex justify-between mb-2">
                  <span className="text-[10px] text-[#8A8A8A] uppercase tracking-[0.18em]">FAQ #{i + 1}</span>
                  <button onClick={() => removeFaq(i)} className="text-[10px] text-[#B8342A] uppercase tracking-[0.18em]" data-testid={`ga-faq-del-${i}`}>Remove</button>
                </div>
                <I label={`Q (${activeLang})`} value={f.q?.[activeLang] || ""} on={(v) => setFaq(i, "q", activeLang, v)} testid={`ga-faq-q-${i}-${activeLang}`} />
                <T label={`A (${activeLang})`} value={f.a?.[activeLang] || ""} on={(v) => setFaq(i, "a", activeLang, v)} testid={`ga-faq-a-${i}-${activeLang}`} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Label = ({ children }) => <label className="text-[10px] text-[#8A8A8A] tracking-[0.18em] uppercase block mb-1">{children}</label>;
function I({ label, value, on, testid }) {
  return <div className="mt-3"><Label>{label}</Label><input value={value || ""} onChange={(e) => on(e.target.value)} data-testid={testid} className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm" /></div>;
}
function T({ label, value, on, rows = 4, testid }) {
  return <div className="mt-3"><Label>{label}</Label><textarea value={value || ""} onChange={(e) => on(e.target.value)} rows={rows} data-testid={testid} className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 py-2 text-sm font-mono" /></div>;
}
